import { Component, Element, Event, EventEmitter, Method, Prop, State, Watch, h } from '@stencil/core';

import Bind from 'bind-decorator';
import { ZdsInputElement } from '../../types';
import { AriaObject, processAriaObject } from '../../utils/aria-object';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { generateUniqueUuid } from '../../utils/generate-unique-uuid';
import { genericRenderLabel } from '../../utils/generic-render-label';
import {
	onGenericSubInputBlur,
	onGenericSubInputFocus,
	onGenericSubInputInput,
} from '../../utils/generic-sub-input-events';
import {
	ValidationEvent,
	ValidationFunction,
	ValidationMessages,
	ValidationState,
	getValidationCustomMessages,
	getValidationState,
	validate,
} from '../../utils/input-validation';
import { hasSlotted } from '../../utils/slotted';

export interface CheckboxChangeEventData {
	shiftKey: boolean;
}

@Component({
	shadow: true,
	styleUrl: 'zds-checkbox.scss',
	tag: 'zds-checkbox',
})
export class ZdsCheckbox implements ZdsInputElement {
	inputElement?: HTMLInputElement;
	hasLoaded: boolean;
	validationMessages: ValidationMessages;
	validationFunctions: ValidationFunction[] = [() => getValidationState(this.inputElement!.validity)];

	@Element() hostElement: HTMLElement;

	@Event() zdsBlur: EventEmitter;
	@Event() zdsFocus: EventEmitter;
	@Event({ bubbles: true }) zdsChange: EventEmitter<CheckboxChangeEventData>;
	@Event({ bubbles: true }) zdsInput: EventEmitter;
	@Event({ bubbles: true }) zdsInputDidUpdate: EventEmitter;
	@Event({ bubbles: true }) zdsFormDoSubmit: EventEmitter;
	@Event() zdsValidate: EventEmitter<ValidationEvent>;

	@Prop({ reflect: true, mutable: true }) checked: boolean;
	@Prop() disabled: boolean;
	@Prop() forceHover: boolean; // undocumented prop for parent elements to control the hover state
	@Prop() indeterminate: boolean;
	@Prop() inputAria: AriaObject | string;
	@Prop() label: string;
	@Prop() name: string;
	@Prop() noAsterisk: boolean = false;
	@Prop() required: boolean;
	@Prop() value: string;
	@Prop() zdsId: string = generateUniqueUuid('zds-checkbox');

	@State() focused: boolean;
	@State() touched: boolean = false;
	@State() validationState: ValidationState = {
		message: '',
		reason: '',
		valid: false,
	};

	@Watch('checked')
	onCheckedChange(newValue: boolean, currentValue: boolean) {
		if (currentValue === newValue) return;
		this.toggle(newValue, { isUserInput: false });
	}

	@Watch('indeterminate')
	onIndeterminateChange(newValue: boolean) {
		this.inputElement!.indeterminate = newValue;
	}

	@Method()
	async reset() {
		this.checked = false;
		this.touched = false;

		await this.validate();
	}

	@Method()
	async validate(forceTouched?: boolean, forcedValidState?: boolean) {
		this.touched = forceTouched ?? this.touched;

		const value = this.checked ? this.value : '';
		this.validationState = validate(
			this.validationFunctions,
			this.validationMessages,
			value,
			null,
			this.disabled,
			false,
		);

		/**
		 * We don't want to emit a zdsValidate if it is in a checkbox-group. This is because the checkbox-group already fires a zdsValidate.
		 */
		if (this.validationState.valid && !this.hostElement.closest('zds-checkbox-group')) {
			const event = this.zdsValidate.emit({
				value: value,
				viewValue: null,
			});
			if (event.detail.validationState) {
				this.validationState = event.detail.validationState;

				if (!this.validationState.valid && this.validationMessages[this.validationState.reason]) {
					this.validationState.message = this.validationMessages[this.validationState.reason];
				}
			}
		}

		if (typeof forcedValidState !== 'undefined') {
			this.validationState.valid = forcedValidState;
		}

		return this.validationState.valid;
	}

	@Bind
	async onBlur(event: FocusEvent) {
		await this.validate();

		onGenericSubInputBlur(this, event);
	}

	@Bind
	async onChange(event: KeyboardEvent) {
		event.stopPropagation();
		this.toggle(this.inputElement!.checked, { shiftKey: event.shiftKey });
	}

	@Bind
	async onLabelClick(event: MouseEvent) {
		event.preventDefault();

		// Pressing shift while clicking the checkbox might select text, so prevent this
		document.getSelection()?.removeAllRanges();

		if (this.disabled) return;

		this.toggle(!this.inputElement!.checked, { shiftKey: event.shiftKey });
	}

	@Bind
	onFocus(event: FocusEvent) {
		onGenericSubInputFocus(this, event);
	}

	@Bind
	onInput(event: InputEvent) {
		onGenericSubInputInput(this, event);
	}

	@Bind
	onKeyDown(event: KeyboardEvent) {
		switch (event.key) {
			case 'Enter':
				this.zdsFormDoSubmit.emit();
				return;
			case ' ':
				this.toggle(!this.inputElement!.checked, { shiftKey: event.shiftKey });
				return;
		}
	}

	connectedCallback(): void {
		this.inputDidUpdate();
	}

	componentDidLoad() {
		this.inputElement!.indeterminate = this.indeterminate;

		// Disabled animations on page load
		setTimeout(() => {
			this.hasLoaded = true;
		}, 1);
	}

	componentWillLoad(): void {
		this.validationMessages = getValidationCustomMessages(this.hostElement);
	}

	componentDidUpdate() {
		this.inputDidUpdate();
	}

	render() {
		const containerClasses = generateCssClasses('choice-input', {
			isChecked: this.checked,
			isDisabled: this.disabled,
			isInvalid: this.touched && !this.validationState.valid,
			hasLoaded: this.hasLoaded,
		});

		return (
			<div class={containerClasses}>
				{genericRenderLabel(
					this.hostElement,
					this.label,
					`${this.zdsId}-input`,
					{},
					this.required,
					this.noAsterisk,
				)}
				{this.renderError()}
				{this.renderInput()}
			</div>
		);
	}

	renderError() {
		if (!this.touched || this.validationState.valid || !this.validationState.message) return null;

		return (
			<zds-form-error aria-live="assertive" role="alert" id={`${this.zdsId}-feedback`}>
				{this.validationState.message}
			</zds-form-error>
		);
	}

	renderInlineLabel() {
		if (!hasSlotted(this.hostElement)) return null;

		return (
			<div class="choice-input__label-text">
				<slot />
			</div>
		);
	}

	renderInput() {
		const inputAriaProps = processAriaObject(this.inputAria, 'zds-checkbox', 'input-aria');

		const checkMarkClasses = generateCssClasses('choice-input__input-graphic-checkmark', {
			forceHover: this.forceHover,
		});

		return (
			<label
				class="choice-input__label"
				htmlFor={`${this.zdsId}-input`}
				onClick={this.onLabelClick}
				onKeyDown={undefined} // Satisfy Sonar: no keyboard handler needed for accessibility, as keyboard users will interact with the input element
			>
				<div class="choice-input__input-wrapper">
					<input
						aria-describedby={
							this.touched && !this.validationState.valid ? `${this.zdsId}-feedback` : false
						}
						aria-invalid={!this.validationState.valid}
						checked={this.checked}
						class="choice-input__input-native"
						disabled={this.disabled}
						id={`${this.zdsId}-input`}
						name={this.name}
						onBlur={(event) => void this.onBlur(event)}
						onChange={(event) => void this.onChange(event as KeyboardEvent)}
						onFocus={this.onFocus}
						onInput={this.onInput}
						onKeyDown={this.onKeyDown}
						ref={(element) => (this.inputElement = element)}
						required={this.required}
						type="checkbox"
						value={this.value}
						{...inputAriaProps}
					/>
					<div class="choice-input__input-graphic">
						<div class="choice-input__input-graphic-background"></div>
						<div class={checkMarkClasses}></div>
						<div class="choice-input__input-graphic-indeterminate"></div>
					</div>
				</div>
				{this.renderInlineLabel()}
			</label>
		);
	}

	inputDidUpdate() {
		this.zdsInputDidUpdate.emit({
			id: this.zdsId,
			includeInForm: !!this.checked,
		});
	}

	async toggle(
		checked: boolean,
		{ isUserInput = true, shiftKey = false }: { shiftKey?: boolean; isUserInput?: boolean } = {},
	) {
		if (this.checked === checked) {
			return;
		}

		this.checked = this.inputElement!.checked = checked;
		this.value = this.inputElement!.value;
		this.zdsChange.emit({ shiftKey });

		if (isUserInput) {
			this.touched = true;
			this.indeterminate = false;
		}

		await this.validate();
	}
}
